import { useQuery } from '@tanstack/react-query';
import { simulationService } from '~/services';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { useMergedUploads } from '~/components/simulation/shared/useMergedUploads';
import { adaptFileUploadListItem, type UnifiedSimulationUpload } from '~/components/simulation/shared/uploadAdapter';
import type { SimulationServiceVersion } from '~/store/simulation/types';
import type { SimulationServiceV1SimulationFileUploadsListData } from '@shipium-corp/shipium-ts-types';

type FileUploadListItem = SimulationServiceV1SimulationFileUploadsListData[number];

// Adapt the (double-wrapped) v2 file-uploads list into a sorted UnifiedSimulationUpload[].
export function adaptV2UploadList(
  data: SimulationServiceV1SimulationFileUploadsListData[] | undefined,
): UnifiedSimulationUpload[] {
  const items: FileUploadListItem[] = (data ?? []).flat() as FileUploadListItem[];
  return items
    .map(adaptFileUploadListItem)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

// Version-routed upload listing: v2 → v2 file-uploads only; v1 → the merged list. Both hooks run
// every render (Rules of Hooks); the inactive one is gated off via `enabled` / an empty partnerId.
export function useSimulationUploads(
  version: SimulationServiceVersion,
  partnerId: string,
  isShipiumView = false,
): {
  data: UnifiedSimulationUpload[];
  isLoading: boolean;
  isError: boolean;
  isFetching?: boolean;
  refetch?: () => void;
} {
  const v2Query = useQuery({
    ...useQueryCreator(simulationService.fileUploads.list, { args: [] }),
    enabled: version === 'v2',
  });

  // '' partnerId in v2 mode → useMergedUploads' internal `enabled: !!partnerId` no-ops (unmodified).
  const merged = useMergedUploads(version === 'v1' ? partnerId : '', isShipiumView);

  if (version === 'v2') {
    return {
      data: adaptV2UploadList(v2Query.data),
      isLoading: v2Query.isLoading,
      isError: v2Query.isError,
      // isFetching (not isLoading) so the manual Refresh spins on background refetches too.
      isFetching: v2Query.isFetching,
      // Cached list + no focus-refetch, so surface refetch for the "uploaded on the Uploads page" case.
      refetch: v2Query.refetch,
    };
  }
  return merged;
}
